/**
 * PipelineTimeline Component
 *
 * A horizontal timeline showing project progress through pipeline stages.
 * Features:
 * - Current stage always centered in view
 * - All stages show names and completion dates
 * - Click-and-drag scrolling anywhere in the timeline
 * - Line runs full width with color change at current stage
 * - Theme-aware colors using tenant's primary/secondary palette
 * - Smooth animations and pulsing current stage indicator
 */

import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { Check, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, LocateFixed } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";

// ============================================================================
// Types
// ============================================================================

interface Stage {
  id: number;
  name: string;
  stage_order: number;
}

interface StageVisit {
  enteredAt: string;
  completedAt?: string;
}

interface StageHistoryEntry {
  stageId: number;
  stageName: string;
  visits: StageVisit[];
  firstEnteredAt?: string;
  lastEnteredAt?: string;
  lastCompletedAt?: string;
  visitCount: number;
  isRevisited: boolean;
}

interface PipelineTimelineProps {
  /** Current stage ID the project is in */
  currentStageId: number;
  /** All stages in the pipeline */
  stages: Stage[];
  /** When the project entered the current stage */
  stageChangedAt?: string;
  /** History of stage changes (for completion dates) */
  stageHistory?: StageHistoryEntry[];
  /** Optional class name */
  className?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch {
    return "";
  }
}

function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return "";
  try {
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  } catch {
    return "";
  }
}

// ============================================================================
// Component
// ============================================================================

const TIMELINE_COLLAPSED_KEY = "pipeline-timeline-collapsed";

export function PipelineTimeline({
  currentStageId,
  stages,
  stageChangedAt,
  stageHistory = [],
  className,
}: PipelineTimelineProps) {
  const { currentTheme } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const currentStageRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);

  // Collapsed state - persisted in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem(TIMELINE_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      try {
        localStorage.setItem(TIMELINE_COLLAPSED_KEY, String(newValue));
      } catch {
        // Ignore localStorage errors
      }
      return newValue;
    });
  }, []);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);

  // Drag scrolling state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Sort stages by stage_order
  const sortedStages = useMemo(() => {
    return [...stages].sort((a, b) => a.stage_order - b.stage_order);
  }, [stages]);

  // Find current stage index
  const currentStageIndex = useMemo(() => {
    return sortedStages.findIndex((stage) => stage.id === currentStageId);
  }, [sortedStages, currentStageId]);

  // Create a map of stage history for quick lookup
  const stageHistoryMap = useMemo(() => {
    const map = new Map<number, StageHistoryEntry>();
    stageHistory.forEach((entry) => {
      map.set(entry.stageId, entry);
    });
    return map;
  }, [stageHistory]);

  // Categorize stages
  const getStageStatus = (index: number): "completed" | "current" | "future" => {
    if (index < currentStageIndex) return "completed";
    if (index === currentStageIndex) return "current";
    return "future";
  };

  // Get stage history info for a stage
  const getStageHistoryInfo = (stageId: number): StageHistoryEntry | undefined => {
    return stageHistoryMap.get(stageId);
  };

  // Get theme-based colors
  const colors = useMemo(() => {
    const primary = currentTheme?.colors?.primary || "#10B981";
    const secondary = currentTheme?.colors?.secondary || "#3B82F6";

    return {
      completed: primary,
      current: secondary,
      future: "hsl(var(--muted))",
      completedText: "hsl(var(--primary-foreground))",
      currentText: "hsl(var(--secondary-foreground, var(--primary-foreground)))",
      futureText: "hsl(var(--muted-foreground))",
      lineCompleted: primary,
      lineFuture: "hsl(var(--border))",
      labelCompleted: primary,
      labelCurrent: secondary,
      labelFuture: "hsl(var(--muted-foreground))",
    };
  }, [currentTheme]);

  // Check scroll position for arrow visibility and reset button
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    const currentElement = currentStageRef.current;
    if (!container) return;

    const { scrollLeft: sl, scrollWidth, clientWidth } = container;
    setShowLeftArrow(sl > 10);
    setShowRightArrow(sl < scrollWidth - clientWidth - 10);

    // Check if current stage is centered (within tolerance)
    if (currentElement) {
      const containerWidth = container.clientWidth;
      const elementLeft = currentElement.offsetLeft;
      const elementWidth = currentElement.offsetWidth;
      const idealScrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
      const scrollDifference = Math.abs(sl - idealScrollPosition);
      // Show reset button if scrolled more than 50px away from centered position
      setShowResetButton(scrollDifference > 50);
    }
  }, []);

  // Center the current stage in the viewport
  const centerCurrentStage = useCallback(() => {
    if (!currentStageRef.current || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const currentElement = currentStageRef.current;

    // Get the center position of the current stage element
    const containerWidth = container.clientWidth;
    const elementLeft = currentElement.offsetLeft;
    const elementWidth = currentElement.offsetWidth;

    // Calculate scroll position to center the current stage
    const scrollTarget = elementLeft - (containerWidth / 2) + (elementWidth / 2);

    container.scrollTo({
      left: Math.max(0, scrollTarget),
      behavior: "smooth",
    });
  }, []);

  // Center current stage on mount and when it changes
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      centerCurrentStage();
      checkScrollPosition();
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStageIndex, centerCurrentStage, checkScrollPosition]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", checkScrollPosition);

    const handleResize = () => {
      checkScrollPosition();
      centerCurrentStage();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      container.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", handleResize);
    };
  }, [checkScrollPosition, centerCurrentStage]);

  // Drag scrolling handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    container.style.cursor = "grabbing";
    container.style.userSelect = "none";
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Multiply for faster scrolling
    container.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.style.cursor = "grab";
      container.style.userSelect = "";
    }
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      handleMouseUp();
    }
  }, [isDragging, handleMouseUp]);

  // Touch handlers for mobile drag scrolling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setIsDragging(true);
    setStartX(e.touches[0].pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const x = e.touches[0].pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5;
    container.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Arrow scroll handlers
  const handleScrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  // Reset view handler - centers current stage
  const handleResetView = useCallback(() => {
    centerCurrentStage();
    setShowResetButton(false);
  }, [centerCurrentStage]);

  // Don't render if no stages
  if (sortedStages.length === 0) {
    return null;
  }

  // Calculate progress percentage for the line color change
  const progressPercent = currentStageIndex >= 0
    ? ((currentStageIndex) / Math.max(sortedStages.length - 1, 1)) * 100
    : 0;

  // Find current stage name for collapsed view
  const currentStageName = sortedStages[currentStageIndex]?.name || "Unknown";

  return (
    <div className={cn("relative w-full", className)}>
      {/* Timeline content - animated collapse */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isCollapsed ? "max-h-0 opacity-0" : "max-h-[200px] opacity-100"
        )}
      >
        <div className="relative">
          {/* Left scroll arrow - always visible when scrollable */}
          <div
            className={cn(
              "absolute left-0 top-1/2 -translate-y-1/2 z-10 transition-opacity duration-200",
              showLeftArrow ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            <button
              onClick={handleScrollLeft}
              className="p-1.5 rounded-full bg-background/95 backdrop-blur-sm border shadow-md hover:bg-muted transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Right scroll arrow - always visible when scrollable */}
          <div
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 z-10 transition-opacity duration-200",
              showRightArrow ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            <button
              onClick={handleScrollRight}
              className="p-1.5 rounded-full bg-background/95 backdrop-blur-sm border shadow-md hover:bg-muted transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>

          {/* Scrollable container with drag-to-scroll */}
          <div
            ref={scrollContainerRef}
            className={cn(
              "overflow-x-auto scrollbar-hide px-8 pt-2 pb-1",
              isDragging ? "cursor-grabbing" : "cursor-grab"
            )}
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
        {/* Container for stages with centered line */}
        <div className="flex items-start min-w-max relative pt-1 pb-1">
          {/* Full track line - grey base from start to finish */}
          {/* Line should be at center of 32px circles: 4px padding (pt-1) + 16px (half circle) = 20px */}
          <div
            className="absolute h-[2px] rounded-full"
            style={{
              backgroundColor: colors.lineFuture,
              top: "20px", // 4px (pt-1) + 16px (center of 32px circle)
              left: "50px", // Start from center of first stage (half of 100px min-width)
              right: "50px", // End at center of last stage
            }}
          />

          {/* Completed/highlighted portion of the line - connects to current stage */}
          <div
            className="absolute h-[2px] rounded-full transition-all duration-700 ease-out"
            style={{
              backgroundColor: colors.lineCompleted,
              boxShadow: `0 0 8px ${colors.lineCompleted}40`,
              top: "20px", // Same as base line
              left: "50px",
              // Width needs to reach the center of the current stage
              // The line spans from 50px to (container - 50px), so total span is (100% - 100px)
              // progressPercent gives us the fraction of that span to cover
              width: sortedStages.length > 1
                ? `calc((100% - 100px) * ${progressPercent / 100})`
                : "0px",
            }}
          />

          {/* Stage nodes */}
          {sortedStages.map((stage, index) => {
            const status = getStageStatus(index);
            const isCurrent = status === "current";
            const isCompleted = status === "completed";
            const isFuture = status === "future";
            const historyInfo = getStageHistoryInfo(stage.id);
            const visitCount = historyInfo?.visitCount || 0;
            const isRevisited = historyInfo?.isRevisited || false;
            const lastCompletedAt = historyInfo?.lastCompletedAt;
            const lastEnteredAt = historyInfo?.lastEnteredAt;

            // Determine what date to show
            // For completed stages: show when it was completed (exit date)
            // For current stage: show when it was entered (from stageChangedAt or history)
            let dateLabel = "";
            if (isCompleted && lastCompletedAt) {
              // Show the completion date for completed stages
              dateLabel = formatDate(lastCompletedAt);
            } else if (isCurrent) {
              // For current stage: prefer stageChangedAt (from project), fallback to history entry date
              if (stageChangedAt) {
                dateLabel = formatDate(stageChangedAt);
              } else if (lastEnteredAt) {
                dateLabel = formatDate(lastEnteredAt);
              }
            }

            return (
              <div
                key={stage.id}
                ref={isCurrent ? currentStageRef : null}
                className={cn(
                  "flex flex-col items-center relative z-[1]",
                  "min-w-[100px] sm:min-w-[120px]",
                )}
              >
                {/* Stage node */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={cn(
                        "relative flex items-center justify-center rounded-full transition-all duration-300",
                        // All circles are 32px (w-8 h-8) so they center on the line
                        "w-8 h-8",
                        // Future stages get a border outline
                        isFuture && "border-2 border-border bg-background",
                      )}
                      style={{
                        backgroundColor: isCompleted
                          ? colors.completed
                          : isCurrent
                            ? colors.current
                            : undefined, // Let className handle future stages
                        boxShadow: isCurrent
                          ? `0 0 0 4px ${colors.current}30, 0 4px 12px ${colors.current}40`
                          : isCompleted
                            ? `0 2px 6px ${colors.completed}25`
                            : "none",
                        // Make current stage slightly larger with transform (keeps center aligned)
                        transform: isCurrent ? "scale(1.25)" : "scale(1)",
                      }}
                      aria-label={`${stage.name} - ${status}`}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      {/* Completed checkmark */}
                      {isCompleted && (
                        <Check
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                          style={{ color: colors.completedText }}
                        />
                      )}

                      {/* Current stage with pulse animation */}
                      {isCurrent && (
                        <>
                          {/* Pulse rings */}
                          <span
                            className="absolute inset-0 rounded-full animate-ping opacity-25"
                            style={{ backgroundColor: colors.current }}
                          />
                          <span
                            className="absolute inset-[-3px] rounded-full animate-pulse opacity-15"
                            style={{ backgroundColor: colors.current }}
                          />
                          {/* Stage number - white text for visibility */}
                          <span
                            className="relative text-xs font-semibold z-10 text-white"
                          >
                            {index + 1}
                          </span>
                        </>
                      )}

                      {/* Future stage number */}
                      {isFuture && (
                        <span
                          className="text-xs font-medium"
                          style={{ color: colors.futureText }}
                        >
                          {index + 1}
                        </span>
                      )}

                      {/* Revisit count badge */}
                      {isRevisited && (
                        <span
                          className="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold rounded-full bg-amber-500 text-white shadow-sm"
                          title={`Visited ${visitCount} times`}
                        >
                          ×{visitCount}
                        </span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    className="max-w-[280px] z-[9999]"
                    sideOffset={8}
                  >
                    <div className="text-center space-y-1.5 py-1">
                      <p className="font-semibold text-sm">{stage.name}</p>
                      <div className="text-xs">
                        {isCompleted && (
                          <div className="space-y-0.5">
                            <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                              <Check className="w-3 h-3" /> Completed
                            </span>
                            {lastCompletedAt && (
                              <p className="text-muted-foreground">{formatDateTime(lastCompletedAt)}</p>
                            )}
                          </div>
                        )}
                        {isCurrent && (
                          <div className="space-y-0.5">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">● Current Stage</span>
                            {stageChangedAt && (
                              <p className="text-muted-foreground">Since {formatDateTime(stageChangedAt)}</p>
                            )}
                          </div>
                        )}
                        {isFuture && (
                          <span className="text-muted-foreground">○ Not Started</span>
                        )}
                      </div>

                      {/* Full visit history for revisited stages */}
                      {isRevisited && historyInfo && (
                        <div className="mt-2 pt-2 border-t border-border/50">
                          <p className="text-[10px] font-medium text-amber-600 dark:text-amber-400 mb-1">
                            Visited {visitCount} times
                          </p>
                          <div className="space-y-1 text-[10px] text-left">
                            {historyInfo.visits.map((visit, visitIdx) => (
                              <div key={visitIdx} className="flex items-start gap-1.5">
                                <span className="text-muted-foreground font-medium shrink-0">
                                  #{visitIdx + 1}:
                                </span>
                                <span className="text-muted-foreground">
                                  {visit.enteredAt ? formatDate(visit.enteredAt) : "?"}
                                  {" → "}
                                  {visit.completedAt ? formatDate(visit.completedAt) : (
                                    <span className="text-blue-500">In Progress</span>
                                  )}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>

                {/* Stage name and date label - shown for ALL stages */}
                <div className="mt-2 flex flex-col items-center text-center px-1">
                  {/* Fixed height container for stage name to align dates */}
                  <div className="h-[28px] sm:h-[32px] flex items-start justify-center">
                    <span
                      className={cn(
                        "text-[10px] sm:text-xs font-medium leading-tight",
                        "max-w-[90px] sm:max-w-[110px]",
                        "line-clamp-2",
                        isCurrent && "font-semibold"
                      )}
                      style={{
                        color: isCompleted
                          ? colors.labelCompleted
                          : isCurrent
                            ? colors.labelCurrent
                            : colors.labelFuture
                      }}
                      title={stage.name}
                    >
                      {stage.name}
                    </span>
                  </div>
                  {/* Date label for completed and current stages */}
                  {dateLabel ? (
                    <span className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight">
                      {dateLabel}
                    </span>
                  ) : (
                    // Placeholder to maintain alignment
                    <span className="text-[9px] sm:text-[10px] text-transparent leading-tight select-none">
                      —
                    </span>
                  )}
                </div>
              </div>
            );
          })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar - toggle, reset, and stage count */}
      <div className="relative flex items-center justify-center">
        {/* Left - current stage info (shown when collapsed) */}
        <div
          className={cn(
            "absolute left-2 flex items-center gap-2 text-xs transition-opacity duration-300",
            isCollapsed ? "opacity-100" : "opacity-0 pointer-events-none"
          )}
        >
          <span
            className="font-medium"
            style={{ color: colors.current }}
          >
            {currentStageName}
          </span>
          {stageChangedAt && (
            <span className="text-muted-foreground text-[10px]">
              ({formatDate(stageChangedAt)})
            </span>
          )}
        </div>

        {/* Center - toggle and reset buttons */}
        <div className="flex items-center gap-1">
          {/* Reset view button - only shows when scrolled away and timeline is expanded */}
          <button
            onClick={handleResetView}
            className={cn(
              "flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-all duration-200",
              showResetButton && !isCollapsed ? "opacity-100" : "opacity-0 pointer-events-none w-0 px-0 overflow-hidden"
            )}
            aria-label="Center current stage"
          >
            <LocateFixed className="h-3 w-3" />
            <span>Reset</span>
          </button>

          {/* Separator - only shows when reset button is visible */}
          <span
            className={cn(
              "text-border text-[10px] transition-opacity duration-200",
              showResetButton && !isCollapsed ? "opacity-100" : "opacity-0"
            )}
          >
            •
          </span>

          {/* Toggle collapse button */}
          <button
            onClick={toggleCollapsed}
            className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
            aria-label={isCollapsed ? "Expand timeline" : "Collapse timeline"}
          >
            {isCollapsed ? (
              <>
                <ChevronDown className="h-3 w-3" />
                <span>Show</span>
              </>
            ) : (
              <>
                <ChevronUp className="h-3 w-3" />
                <span>Hide</span>
              </>
            )}
          </button>
        </div>

        {/* Right - stage count */}
        <div className="absolute right-2 text-[10px] text-muted-foreground bg-background px-1.5 py-0.5 rounded border">
          {currentStageIndex + 1} / {sortedStages.length}
        </div>
      </div>

      {/* CSS for hiding scrollbar */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}

export default PipelineTimeline;
